import React, { useState } from 'react';
import type { VisionGeneration } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import Alert from '../alert/Alert';
import InfoButton from '../../global/buttons/InfoButton';

export const VisionGenerationInfo = ({ generation, onRetry, disabled }: {
  generation?: VisionGeneration; onRetry?: () => void; disabled?: boolean;
}): JSX.Element | null => {
  const { translate: t } = useI18n();
  const [ showSource, setShowSource ] = useState(false);
  if (!generation) {
    return null;
  }
  const { status, provenance } = generation;
  const sourceKey = provenance?.sourceType.toUpperCase().replace(/-/g, '_');
  return (
    <div className={'text-sm space-y-2'}>
      {[ 'pending', 'running', 'failed' ].includes(status) && <p role={'status'}>{t(`pages.VISION_GENERATION.${status.toUpperCase()}`)}</p>}
      {status === 'failed' && onRetry && <button type={'button'} disabled={disabled} onClick={onRetry}>{t('pages.VISION_GENERATION.RETRY')}</button>}
      {provenance && (
        <>
          <div className={'flex items-center gap-2 text-xs text-gray-500'}>
            <span aria-hidden>✦</span>
            <span>{t(`pages.VISION_GENERATION.SHORT.${sourceKey}`)}</span>
            <InfoButton
              aria-label={t('pages.VISION_GENERATION.SOURCE')}
              aria-expanded={showSource}
              size={'sm'}
              onClick={() => setShowSource((value) => !value)}
            />
          </div>
          <Alert show={showSource} disableIcon>
            <section aria-label={t('pages.VISION_GENERATION.SOURCE')} className={'text-sm space-y-2'}>
              <p>
                <strong><em>{t('pages.VISION_GENERATION.SOURCE')}</em></strong>
                {' - '}{t(`pages.VISION_GENERATION.${sourceKey}`)}.
                {provenance.sourceType === 'goals-generated' && <> {t('pages.VISION_GENERATION.GOALS_ONLY')}</>}
              </p>
              {provenance.sources.filter(({ url }) => {
                return /^https?:\/\//i.test(url);
              }).map(({ url, title }) => {
                return (
                  <p key={url}>
                    <a className={'underline break-words'} href={url} target={'_blank'} rel={'noopener noreferrer'}>
                      {title && <span className={'block'}>{title}</span>}
                      <span>{url}</span>
                    </a>
                  </p>
                );
              })}
              {provenance.originalText && provenance.sourceType !== 'copied' && (
                <details>
                  <summary className={'cursor-pointer'}>{t('pages.VISION_GENERATION.ORIGINAL_TEXT')}</summary>
                  <blockquote className={'border-l-2 pl-3 mt-2 whitespace-pre-wrap'}>{provenance.originalText}</blockquote>
                </details>
              )}
            </section>
          </Alert>
        </>
      )}
    </div>
  );
};
